import React, { useEffect, useState, useMemo } from 'react';
import { Bed, Truck, MagnifyingGlass, ArrowsClockwise, DownloadSimple, Check, X, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * StallsTab — paid stall & RV reservations for a show.
 *
 * Producer/office view that lists every rider who has reserved (and paid
 * for) a stall or RV spot. Shows pending reservations too (riders who
 * started checkout but haven't paid yet) so the office can follow up.
 *
 * Sourced from `GET /api/shows/{id}/spot-reservations`, which the office
 * is already authorized to call (producer / OFFICE_STAFF / org admin).
 */
export const StallsTab = ({ showId, showName }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('PAID'); // PAID | PENDING | ALL

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shows/${showId}/spot-reservations`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load stall reservations');
    } finally {
      setLoading(false);
    }
  };

  // Per-row producer actions: flip paid/unpaid, cancel altogether.
  // Optimistic local update keeps the UI snappy; a full reload pulls the
  // canonical state from the server (and updates counters via the new
  // _refresh_show_counters helper on the backend).
  const [rowBusy, setRowBusy] = useState(null);

  const markPaid = async (r) => {
    setRowBusy(r.id);
    try {
      await api.patch(`/spot-reservations/${r.id}/mark-paid`);
      toast.success(`Marked ${r.rider_name}'s ${r.type} ${r.row}-${r.spot_number} as PAID`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setRowBusy(null);
    }
  };

  const markUnpaid = async (r) => {
    if (!window.confirm(`Mark ${r.rider_name}'s ${r.type} ${r.row}-${r.spot_number} as UNPAID? This flips the local status back to pending — the Stripe payment (if any) is not refunded.`)) return;
    setRowBusy(r.id);
    try {
      await api.patch(`/spot-reservations/${r.id}/mark-unpaid`);
      toast.success(`Marked as UNPAID`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark unpaid');
    } finally {
      setRowBusy(null);
    }
  };

  const cancelReservation = async (r) => {
    const isPaid = r.status === 'paid' && !r.reserved_by_producer;
    const msg = isPaid
      ? `Cancel a PAID reservation? Only producer holds can be cancelled while paid. For real rider payments, refund via the office first.`
      : `Cancel ${r.rider_name}'s ${r.type} ${r.row}-${r.spot_number}? This removes the record permanently.`;
    if (!window.confirm(msg)) return;
    setRowBusy(r.id);
    try {
      await api.delete(`/spot-reservations/${r.id}`);
      toast.success('Reservation cancelled');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    } finally {
      setRowBusy(null);
    }
  };

  useEffect(() => { load(); }, [showId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filter === 'PAID' && r.status !== 'paid') return false;
      if (filter === 'PENDING' && r.status !== 'pending') return false;
      if (search.trim()) {
        const hay = `${r.rider_name || ''} ${r.barn || ''} ${r.row || ''} ${r.spot_number || ''}`.toLowerCase();
        if (!hay.includes(search.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, search, filter]);

  // Summary stats based on the unfiltered list so the producer can see
  // the totals at a glance regardless of the filter chip selected.
  const stats = useMemo(() => {
    const paid = rows.filter((r) => r.status === 'paid');
    const pending = rows.filter((r) => r.status === 'pending');
    const paidStalls = paid.filter((r) => r.type === 'stall').length;
    const paidRV = paid.filter((r) => r.type === 'rv').length;
    const paidRevenue = paid.reduce((s, r) => s + Number(r.fee || 0), 0);
    return {
      paidCount: paid.length,
      paidStalls,
      paidRV,
      pendingCount: pending.length,
      paidRevenue,
    };
  }, [rows]);

  const exportCsv = () => {
    const lines = [
      ['Rider', 'Type', 'Location', 'Spot', 'Fee', 'Pricing', 'Nights', 'Status', 'Reserved At', 'Paid At', 'Producer Hold', 'Notes'].join(','),
      ...filteredRows.map((r) => [
        csvCell(r.rider_name),
        csvCell(r.type),
        csvCell(r.barn),
        csvCell(`${r.row}-${r.spot_number}`),
        Number(r.fee || 0).toFixed(2),
        csvCell(r.pricing_type),
        r.nights ?? '',
        csvCell(r.status),
        csvCell(r.reserved_at),
        csvCell(r.paid_at),
        r.reserved_by_producer ? 'YES' : '',
        csvCell(r.staff_notes),
      ].join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(showName || 'show').replace(/\s+/g, '_')}_stalls.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="stalls-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Paid Stalls" value={stats.paidStalls} color="#34C759" testid="stalls-tab-paid-stalls" />
        <StatCard label="Paid RV" value={stats.paidRV} color="#5E5CE6" testid="stalls-tab-paid-rv" />
        <StatCard label="Pending" value={stats.pendingCount} color="#FF9500" testid="stalls-tab-pending" />
        <StatCard label="Paid Revenue" value={`$${stats.paidRevenue.toFixed(0)}`} color="#FFB000" testid="stalls-tab-revenue" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rider or spot…"
            className="w-full pl-8 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#FF9500]"
            data-testid="stalls-tab-search"
          />
        </div>
        <div className="flex bg-[#0A0A0A] border border-[#262626] rounded p-0.5">
          {['PAID', 'PENDING', 'ALL'].map((opt) => (
            <button
              key={opt}
              onClick={() => setFilter(opt)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition-colors ${
                filter === opt ? 'bg-[#FF9500] text-black' : 'text-[#9E9E9E] hover:text-white'
              }`}
              data-testid={`stalls-tab-filter-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="px-2.5 py-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#FF9500] text-[#9E9E9E] hover:text-white rounded transition-colors"
          title="Refresh"
          data-testid="stalls-tab-refresh"
        >
          <ArrowsClockwise size={14} />
        </button>
        <button
          onClick={exportCsv}
          disabled={filteredRows.length === 0}
          className="flex items-center gap-1.5 px-2.5 py-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#34C759] disabled:opacity-50 text-[#9E9E9E] hover:text-white text-[11px] font-bold rounded transition-colors"
          data-testid="stalls-tab-export"
        >
          <DownloadSimple size={14} /> CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-8 text-center text-[#9E9E9E] text-sm">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div className="py-12 text-center" data-testid="stalls-tab-empty">
          <Bed size={32} className="text-[#262626] mx-auto mb-2" />
          <p className="text-sm text-[#9E9E9E]">No reservations match your filters.</p>
        </div>
      ) : (
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded overflow-hidden overflow-x-auto">
          <table className="w-full text-xs" data-testid="stalls-tab-table">
            <thead className="bg-[#161616] text-left">
              <tr className="text-[10px] uppercase text-[#9E9E9E]">
                <th className="px-3 py-2 font-semibold">Rider</th>
                <th className="px-3 py-2 font-semibold">Type</th>
                <th className="px-3 py-2 font-semibold">Location</th>
                <th className="px-3 py-2 font-semibold">Spot</th>
                <th className="px-3 py-2 font-semibold">Pricing</th>
                <th className="px-3 py-2 font-semibold text-right">Fee</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Reserved</th>
                <th className="px-3 py-2 font-semibold">Notes</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} className="border-t border-[#1F1F1F]" data-testid={`stalls-tab-row-${r.id}`}>
                  <td className="px-3 py-2 text-white font-semibold whitespace-nowrap">{r.rider_name}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      {r.type === 'stall' ? <Bed size={12} className="text-[#34C759]" /> : <Truck size={12} className="text-[#5E5CE6]" />}
                      <span className="text-white uppercase text-[10px]">{r.type}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#9E9E9E] whitespace-nowrap">{r.barn || 'Main'}</td>
                  <td className="px-3 py-2 text-white font-mono whitespace-nowrap">{r.row}-{r.spot_number}</td>
                  <td className="px-3 py-2 text-[#9E9E9E] text-[10px] whitespace-nowrap">
                    {r.pricing_type === 'per_day' ? `Per day (${r.nights || '?'}n)` : 'Weekend'}
                  </td>
                  <td className="px-3 py-2 text-white text-right font-bold">${Number(r.fee || 0).toFixed(0)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                        r.status === 'paid'
                          ? 'bg-[#34C759]/15 text-[#34C759]'
                          : 'bg-[#FF9500]/15 text-[#FF9500]'
                      }`}
                    >
                      {r.status.toUpperCase()}
                    </span>
                    {r.reserved_by_producer && (
                      <span className="ml-1 text-[9px] font-bold text-[#AF52DE]">HOLD</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#9E9E9E] text-[10px] whitespace-nowrap">
                    {formatDate(r.reserved_at)}
                  </td>
                  <td className="px-3 py-2 text-[#9E9E9E] text-[10px]">{r.staff_notes || ''}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1 justify-end">
                      {r.status === 'pending' ? (
                        <button
                          onClick={() => markPaid(r)}
                          disabled={rowBusy === r.id}
                          title="Mark this reservation as paid"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#34C759]/15 hover:bg-[#34C759]/25 text-[#34C759] text-[10px] font-bold rounded disabled:opacity-50"
                          data-testid={`stalls-tab-mark-paid-${r.id}`}
                        >
                          <Check size={11} weight="bold" /> Paid
                        </button>
                      ) : (
                        <button
                          onClick={() => markUnpaid(r)}
                          disabled={rowBusy === r.id}
                          title="Flip back to pending — does NOT refund any Stripe payment"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF9500]/15 hover:bg-[#FF9500]/25 text-[#FF9500] text-[10px] font-bold rounded disabled:opacity-50"
                          data-testid={`stalls-tab-mark-unpaid-${r.id}`}
                        >
                          <X size={11} weight="bold" /> Unpaid
                        </button>
                      )}
                      <button
                        onClick={() => cancelReservation(r)}
                        disabled={rowBusy === r.id}
                        title="Cancel & remove this reservation"
                        className="inline-flex items-center px-2 py-1 bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20 text-[#FF3B30] text-[10px] font-bold rounded disabled:opacity-50"
                        data-testid={`stalls-tab-cancel-${r.id}`}
                      >
                        <Trash size={11} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, testid }) => (
  <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded p-3" data-testid={testid}>
    <p className="text-[9px] uppercase tracking-wider text-[#9E9E9E]">{label}</p>
    <p className="text-2xl font-black text-white mt-1" style={{ fontFamily: 'Outfit', color }}>
      {value}
    </p>
  </div>
);

const csvCell = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
};
